import React from 'react';
import styled from 'styled-components';
import { WarningAmber, ErrorOutline, OpenInNew } from '@mui/icons-material';

// Maps Stripe requirement keys to human-readable labels
const REQUIREMENT_LABELS = {
    'individual.id_number': 'Identity (SSN) verification required',
    'individual.ssn_last_4': 'SSN last 4 digits required',
    'individual.verification.document': 'Government-issued ID document required',
    'individual.verification.additional_document': 'Additional identity document required',
    'individual.dob.day': 'Date of birth required',
    'individual.dob.month': 'Date of birth required',
    'individual.dob.year': 'Date of birth required',
    'individual.first_name': 'Legal first name required',
    'individual.last_name': 'Legal last name required',
    'individual.email': 'Email address required',
    'individual.phone': 'Phone number required',
    'individual.address.line1': 'Street address required',
    'individual.address.city': 'City required',
    'individual.address.state': 'State required',
    'individual.address.postal_code': 'ZIP code required',
    'business_profile.url': 'Business website URL required',
    'business_profile.mcc': 'Business category required',
    'external_account': 'Bank account required',
    'tos_acceptance.date': 'Terms of service acceptance required',
    'tos_acceptance.ip': 'Terms of service acceptance required',
};

const getLabel = (key) => {
    // Strip array suffixes like [0] that Stripe sometimes adds
    const clean = key.replace(/\[\d+\]/g, '');
    return REQUIREMENT_LABELS[clean] || key.replace(/_/g, ' ').replace(/\./g, ' › ');
};

// Deduplicate dob fields into one entry
const deduplicateRequirements = (items) => {
    const seen = new Set();
    return items.filter((key) => {
        const label = getLabel(key);
        if (seen.has(label)) return false;
        seen.add(label);
        return true;
    });
};

const BannerWrapper = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 18px;
    border-radius: 10px;
    border: 1px solid ${({ $isDisabled }) => ($isDisabled ? '#fca5a5' : '#fcd34d')};
    background: ${({ $isDisabled }) => ($isDisabled ? '#fef2f2' : '#fffbeb')};
    margin-bottom: 20px;
`;

const IconWrapper = styled.div`
    flex-shrink: 0;
    margin-top: 1px;
    color: ${({ $isDisabled }) => ($isDisabled ? '#dc2626' : '#d97706')};
    display: flex;
    align-items: center;
`;

const Content = styled.div`
    flex: 1;
`;

const Title = styled.p`
    font-size: 14px;
    font-weight: 600;
    color: ${({ $isDisabled }) => ($isDisabled ? '#991b1b' : '#92400e')};
    margin: 0 0 4px 0;
`;

const Body = styled.p`
    font-size: 13px;
    color: ${({ $isDisabled }) => ($isDisabled ? '#b91c1c' : '#a16207')};
    margin: 0 0 10px 0;
    line-height: 1.5;
`;

const RequirementList = styled.ul`
    margin: 0 0 12px 0;
    padding-left: 18px;
    font-size: 13px;
    color: ${({ $isDisabled }) => ($isDisabled ? '#b91c1c' : '#a16207')};

    li {
        margin-bottom: 3px;
        line-height: 1.5;
    }
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    padding: 7px 14px;
    border-radius: 6px;
    border: 1px solid ${({ $isDisabled }) => ($isDisabled ? '#dc2626' : '#d97706')};
    background: transparent;
    color: ${({ $isDisabled }) => ($isDisabled ? '#dc2626' : '#d97706')};
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
        background: ${({ $isDisabled }) => ($isDisabled ? '#fecaca' : '#fef3c7')};
    }
`;

const StripeActionBanner = ({ health, onFixClick }) => {
    if (!health || !health.has_issues) return null;

    const isDisabled = !health.charges_enabled;
    const allDue = deduplicateRequirements([
        ...(health.currently_due ?? []),
        ...(health.past_due ?? []),
    ]);

    const title = isDisabled
        ? 'Your Stripe account cannot accept payments'
        : 'Action required on your Stripe account';

    const body = isDisabled
        ? 'Stripe has disabled payment acceptance on this account. Complete the required actions below to restore payments.'
        : 'Stripe requires the following information before payments can continue. Please complete these items to avoid any disruption.';

    return (
        <BannerWrapper $isDisabled={isDisabled}>
            <IconWrapper $isDisabled={isDisabled}>
                {isDisabled
                    ? <ErrorOutline style={{ fontSize: 20 }} />
                    : <WarningAmber style={{ fontSize: 20 }} />
                }
            </IconWrapper>
            <Content>
                <Title $isDisabled={isDisabled}>{title}</Title>
                <Body $isDisabled={isDisabled}>{body}</Body>
                {allDue.length > 0 && (
                    <RequirementList $isDisabled={isDisabled}>
                        {allDue.map((key) => (
                            <li key={key}>{getLabel(key)}</li>
                        ))}
                    </RequirementList>
                )}
                <ActionButton $isDisabled={isDisabled} onClick={onFixClick}>
                    Complete in Stripe <OpenInNew style={{ fontSize: 14 }} />
                </ActionButton>
            </Content>
        </BannerWrapper>
    );
};

export default StripeActionBanner;
